/**
 * Execution Details Page
 * Displays detailed information about a single campaign execution
 */

import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { ArrowLeft, RefreshCw, Users, Mail, CheckCircle, AlertCircle, Clock } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  StatusBadge,
  ProgressBar,
  MetricCard,
} from '../components'
import {
  getExecutionProgress,
  getCampaignMetrics,
  subscribeToProgressStream,
  getMonitoringExecutionDetail,
} from '../queries/executions'
import type { ExecutionProgress } from '../types'

/**
 * Format duration in human-readable format
 */
function formatDuration(ms: number | null): string {
  if (ms === null || ms === undefined) return '—'

  const seconds = Math.floor(ms / 1000)
  if (seconds < 60) return `${seconds} seconds`

  const minutes = Math.floor(seconds / 60)
  const remainingSeconds = seconds % 60
  if (minutes < 60) return `${minutes}m ${remainingSeconds}s`

  const hours = Math.floor(minutes / 60)
  const remainingMinutes = minutes % 60
  return `${hours}h ${remainingMinutes}m`
}

/**
 * Format timestamp
 */
function formatTimestamp(timestamp: string | null): string {
  if (!timestamp) return '—'
  return new Date(timestamp).toLocaleString()
}

export function ExecutionDetailsPage() {
  const { executionId } = useParams<{ executionId: string }>()
  const navigate = useNavigate()
  const [_liveProgress, setLiveProgress] = useState<ExecutionProgress | null>(null)
  const [isStreaming, setIsStreaming] = useState(false)

  const decodedExecutionId = executionId ? decodeURIComponent(executionId) : ''

  // Fetch detailed execution progress (comprehensive metrics for UI)
  const {
    data: progressData,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ['execution', 'progress', decodedExecutionId],
    queryFn: () => getExecutionProgress(decodedExecutionId),
    enabled: !!decodedExecutionId,
    refetchInterval: 5000,
  })

  // Fetch enhanced monitoring details for A/B test info, iteration details, credit info
  const { data: monitoringDetail } = useQuery({
    queryKey: ['monitoring', 'detail', decodedExecutionId],
    queryFn: () => getMonitoringExecutionDetail(decodedExecutionId),
    enabled: !!decodedExecutionId,
    refetchInterval: 5000,
  })

  // Fetch campaign metrics
  const { data: metrics } = useQuery({
    queryKey: ['campaign', 'metrics', progressData?.campaignId],
    queryFn: () => getCampaignMetrics(progressData?.campaignId || ''),
    enabled: !!progressData?.campaignId,
    refetchInterval: 10000,
  })

  // Use progress data for UI display
  const currentProgress = progressData
  const progress = currentProgress

  // Subscribe to SSE stream for active executions
  useEffect(() => {
    if (!decodedExecutionId || !progress?.isActive) {
      setIsStreaming(false)
      return
    }

    setIsStreaming(true)
    const cleanup = subscribeToProgressStream(
      decodedExecutionId,
      (update) => setLiveProgress(update),
      () => setIsStreaming(false),
      () => {
        setIsStreaming(false)
        setLiveProgress(null)
        refetch()
      }
    )

    return cleanup
  }, [decodedExecutionId, progress?.isActive, refetch])

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto" />
          <p className="mt-4 text-gray-500">Loading execution details...</p>
        </div>
      </div>
    )
  }

  if (error || !currentProgress) {
    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <Button variant="ghost" onClick={() => navigate(-1)} className="mb-4">
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back
        </Button>
        <div className="bg-red-50 border border-red-200 rounded-xl p-6">
          <h2 className="text-lg font-semibold text-red-700">Execution Not Found</h2>
          <p className="text-red-600 mt-2">
            {error instanceof Error ? error.message : `Could not find execution: ${decodedExecutionId}`}
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-6 py-4">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="sm" onClick={() => navigate(-1)}>
            <ArrowLeft className="w-4 h-4" />
          </Button>
          <div className="flex-1">
            <div className="flex items-center gap-3">
              <h1 className="text-xl font-bold text-gray-900">Execution Details</h1>
              <StatusBadge status={currentProgress.status} />
              {isStreaming && (
                <span className="flex items-center gap-1 text-xs text-green-600">
                  <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
                  Live
                </span>
              )}
            </div>
            <p className="text-sm text-gray-500 font-mono mt-1">{decodedExecutionId}</p>
          </div>
          <Button variant="outline" size="sm" onClick={() => refetch()}>
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
        </div>
      </div>

      {/* Main content */}
      <div className="p-6 space-y-6">
        {/* Overview cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <MetricCard
            title="Campaign ID"
            value={currentProgress.campaignId}
            icon={<Mail className="w-5 h-5" />}
            color="blue"
          />
          <MetricCard
            title="Channel"
            value={currentProgress.channelType}
            icon={<Mail className="w-5 h-5" />}
            color="purple"
          />
          <MetricCard
            title="Duration"
            value={formatDuration(currentProgress.elapsedMs)}
            icon={<Clock className="w-5 h-5" />}
            color="gray"
          />
          <MetricCard
            title="Current Phase"
            value={currentProgress.phaseDescription || currentProgress.currentPhase || '—'}
            icon={<Clock className="w-5 h-5" />}
            color="blue"
          />
        </div>

        {/* Progress section */}
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Overall Progress</h2>
          <ProgressBar
            value={currentProgress.overallProgressPct}
            size="lg"
            color={
              currentProgress.status === 'FAILED'
                ? 'red'
                : currentProgress.status === 'COMPLETED'
                  ? 'green'
                  : 'blue'
            }
          />
          {currentProgress.statusSummary && (
            <p className="text-sm text-gray-500 mt-2">{currentProgress.statusSummary}</p>
          )}
        </div>

        {/* Metrics grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Audience metrics */}
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <Users className="w-5 h-5" />
              Audience
            </h2>
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Total Audience</span>
                <span className="font-mono font-semibold">
                  {currentProgress.audienceTotal?.toLocaleString() ?? '—'}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Filtered Out</span>
                <span className="font-mono text-orange-600">
                  {currentProgress.audienceFiltered?.toLocaleString() ?? '—'}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Valid Users</span>
                <span className="font-mono text-green-600">
                  {currentProgress.validUsers?.toLocaleString() ?? '—'}
                </span>
              </div>
            </div>
          </div>

          {/* Message metrics */}
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <Mail className="w-5 h-5" />
              Messages
            </h2>
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Created</span>
                <span className="font-mono font-semibold">
                  {currentProgress.messagesCreated?.toLocaleString() ?? '—'}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Immediate</span>
                <span className="font-mono">
                  {currentProgress.messagesImmediate?.toLocaleString() ?? '—'}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Scheduled</span>
                <span className="font-mono">
                  {currentProgress.messagesScheduled?.toLocaleString() ?? '—'}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Personalization Failed</span>
                <span className="font-mono text-red-600">
                  {currentProgress.personalizationFailed?.toLocaleString() ?? '—'}
                </span>
              </div>
            </div>
          </div>

          {/* Delivery metrics */}
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <CheckCircle className="w-5 h-5" />
              Delivery
            </h2>
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Total for Delivery</span>
                <span className="font-mono font-semibold">
                  {currentProgress.deliveryTotal?.toLocaleString() ?? '—'}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Delivered</span>
                <span className="font-mono text-green-600">
                  {currentProgress.delivered?.toLocaleString() ?? '—'}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Failed</span>
                <span className="font-mono text-red-600">
                  {currentProgress.deliveryFailed?.toLocaleString() ?? '—'}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Pending</span>
                <span className="font-mono text-yellow-600">
                  {currentProgress.deliveryPending?.toLocaleString() ?? '—'}
                </span>
              </div>
            </div>
          </div>

          {/* Batch progress */}
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <Clock className="w-5 h-5" />
              Batch Progress
            </h2>
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Current Batch</span>
                <span className="font-mono font-semibold">
                  {currentProgress.currentBatch ?? '—'}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Total Batches</span>
                <span className="font-mono">{currentProgress.totalBatches ?? '—'}</span>
              </div>
              {currentProgress.currentBatch && currentProgress.totalBatches && (
                <ProgressBar
                  value={(currentProgress.currentBatch / currentProgress.totalBatches) * 100}
                  size="sm"
                  color="indigo"
                />
              )}
            </div>
          </div>
        </div>

        {/* Timing information */}
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Timing</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div>
              <span className="text-sm text-gray-500">Started At</span>
              <p className="font-mono mt-1">{formatTimestamp(currentProgress.startedAt)}</p>
            </div>
            <div>
              <span className="text-sm text-gray-500">Completed At</span>
              <p className="font-mono mt-1">{formatTimestamp(currentProgress.completedAt)}</p>
            </div>
            <div>
              <span className="text-sm text-gray-500">Duration</span>
              <p className="font-mono mt-1">{formatDuration(currentProgress.elapsedMs)}</p>
            </div>
          </div>
        </div>

        {/* Error section (if failed) */}
        {currentProgress.errorMessage && (
          <div className="bg-red-50 border border-red-200 rounded-xl p-6">
            <h2 className="text-lg font-semibold text-red-700 flex items-center gap-2">
              <AlertCircle className="w-5 h-5" />
              Error Details
            </h2>
            <p className="text-red-600 mt-2 font-mono text-sm whitespace-pre-wrap">
              {currentProgress.errorMessage}
            </p>
          </div>
        )}

        {/* A/B Test Details (if available) */}
        {monitoringDetail?.abTestDetails && (
          <div className="bg-orange-50 border border-orange-200 rounded-xl p-6">
            <h2 className="text-lg font-semibold text-orange-700 mb-4 flex items-center gap-2">
              🧪 A/B Test Details
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Current Phase</span>
                  <span className="font-mono font-semibold px-2 py-1 bg-orange-100 rounded">
                    {monitoringDetail.abTestDetails.phase || '—'}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Sample Size</span>
                  <span className="font-mono">
                    {monitoringDetail.abTestDetails.config?.sampleSizePercentage}%
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Smart Selection</span>
                  <span className="font-mono">
                    {monitoringDetail.abTestDetails.config?.smartSelection ? 'Yes' : 'No'}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Winning Metric</span>
                  <span className="font-mono">
                    {monitoringDetail.abTestDetails.config?.winningMetric || '—'}
                  </span>
                </div>
              </div>
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Phase 1 User Count</span>
                  <span className="font-mono font-semibold">
                    {monitoringDetail.abTestDetails.phase1UserCount?.toLocaleString() || '—'}
                  </span>
                </div>
                {monitoringDetail.abTestDetails.winnerVariantId && (
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">Winner Variant</span>
                    <span className="font-mono text-green-600 font-semibold">
                      {monitoringDetail.abTestDetails.winnerVariantId}
                    </span>
                  </div>
                )}
                {monitoringDetail.abTestDetails.phase1CompletedAt && (
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">Phase 1 Completed</span>
                    <span className="font-mono text-sm">
                      {formatTimestamp(monitoringDetail.abTestDetails.phase1CompletedAt)}
                    </span>
                  </div>
                )}
              </div>
            </div>
            {/* Variants */}
            {monitoringDetail.abTestDetails.config?.variants && monitoringDetail.abTestDetails.config.variants.length > 0 && (
              <div className="mt-4 pt-4 border-t border-orange-200">
                <h3 className="text-sm font-semibold text-gray-700 mb-3">Variants</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                  {monitoringDetail.abTestDetails.config.variants.map((variant) => (
                    <div
                      key={variant.variantId}
                      className={`p-3 rounded-lg ${
                        variant.isControl
                          ? 'bg-blue-100 border border-blue-300'
                          : 'bg-white border border-gray-200'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <span className="font-medium">{variant.variantName}</span>
                        {variant.isControl && (
                          <span className="text-xs bg-blue-500 text-white px-2 py-0.5 rounded">
                            Control
                          </span>
                        )}
                      </div>
                      <div className="text-sm text-gray-500 mt-1">
                        {variant.percentageSplit}% traffic
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Iteration Details (for recurring campaigns) */}
        {monitoringDetail?.iterationDetails && (
          <div className="bg-purple-50 border border-purple-200 rounded-xl p-6">
            <h2 className="text-lg font-semibold text-purple-700 mb-4 flex items-center gap-2">
              🔄 Iteration Details
            </h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="bg-white rounded-lg p-4 border border-purple-100">
                <span className="text-sm text-gray-500">Iteration</span>
                <p className="text-xl font-bold text-purple-700 mt-1">
                  #{monitoringDetail.iterationDetails.iterationNumber}
                </p>
              </div>
              <div className="bg-white rounded-lg p-4 border border-purple-100">
                <span className="text-sm text-gray-500">Status</span>
                <p className="text-lg font-semibold text-gray-900 mt-1">
                  {monitoringDetail.iterationDetails.status || '—'}
                </p>
              </div>
              <div className="bg-white rounded-lg p-4 border border-purple-100">
                <span className="text-sm text-gray-500">Audience Size</span>
                <p className="text-lg font-semibold text-gray-900 mt-1">
                  {monitoringDetail.iterationDetails.audienceSize?.toLocaleString() || '—'}
                </p>
              </div>
              <div className="bg-white rounded-lg p-4 border border-purple-100">
                <span className="text-sm text-gray-500">Delivered</span>
                <p className="text-lg font-semibold text-green-600 mt-1">
                  {monitoringDetail.iterationDetails.messagesDelivered?.toLocaleString() || '—'}
                </p>
              </div>
            </div>
            <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
              <div>
                <span className="text-gray-500">Scheduled At</span>
                <p className="font-mono mt-1">
                  {formatTimestamp(monitoringDetail.iterationDetails.scheduledAt)}
                </p>
              </div>
              <div>
                <span className="text-gray-500">Started At</span>
                <p className="font-mono mt-1">
                  {formatTimestamp(monitoringDetail.iterationDetails.startedAt)}
                </p>
              </div>
              <div>
                <span className="text-gray-500">Triggered By</span>
                <p className="font-mono mt-1">
                  {monitoringDetail.iterationDetails.triggeredBy || '—'}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Credit Info (if available) */}
        {monitoringDetail?.creditInfo && monitoringDetail.creditInfo.reservationId && (
          <div className="bg-gray-50 border border-gray-200 rounded-xl p-6">
            <h2 className="text-lg font-semibold text-gray-700 mb-4">Credit Reservation</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <span className="text-sm text-gray-500">Reservation ID</span>
                <p className="font-mono text-sm mt-1 truncate">
                  {monitoringDetail.creditInfo.reservationId}
                </p>
              </div>
              <div>
                <span className="text-sm text-gray-500">Reserved Credits</span>
                <p className="font-semibold mt-1">
                  {monitoringDetail.creditInfo.reservedCredits?.toLocaleString() || '—'}
                </p>
              </div>
              <div>
                <span className="text-sm text-gray-500">Status</span>
                <p className="font-semibold mt-1">
                  {monitoringDetail.creditInfo.status || '—'}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Exclusion Details (if available) */}
        {monitoringDetail?.exclusionsByFilter && Object.keys(monitoringDetail.exclusionsByFilter).length > 0 && (
          <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-6">
            <h2 className="text-lg font-semibold text-yellow-700 mb-4">Exclusions by Filter</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {Object.entries(monitoringDetail.exclusionsByFilter).map(([filter, count]) => (
                <div key={filter} className="bg-white rounded-lg p-3 border border-yellow-100">
                  <span className="text-sm text-gray-500">{filter}</span>
                  <p className="text-lg font-semibold text-yellow-700 mt-1">
                    {(count as number).toLocaleString()}
                  </p>
                </div>
              ))}
            </div>
            {monitoringDetail.totalExcluded != null && (
              <div className="mt-4 pt-4 border-t border-yellow-200 flex justify-between items-center">
                <span className="font-medium text-gray-700">Total Excluded</span>
                <span className="text-xl font-bold text-yellow-700">
                  {monitoringDetail.totalExcluded.toLocaleString()}
                </span>
              </div>
            )}
          </div>
        )}

        {/* Pipeline metrics (from dashboard API) */}
        {metrics && (
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Pipeline Metrics</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="bg-gray-50 rounded-lg p-4">
                <span className="text-sm text-gray-500">Total Messages</span>
                <p className="text-xl font-bold text-gray-900 mt-1">
                  {metrics.totalMessages?.toLocaleString()}
                </p>
              </div>
              <div className="bg-green-50 rounded-lg p-4">
                <span className="text-sm text-gray-500">Delivered</span>
                <p className="text-xl font-bold text-green-700 mt-1">
                  {metrics.deliveredMessages?.toLocaleString()}
                </p>
              </div>
              <div className="bg-yellow-50 rounded-lg p-4">
                <span className="text-sm text-gray-500">Pending</span>
                <p className="text-xl font-bold text-yellow-700 mt-1">
                  {metrics.pendingMessages?.toLocaleString()}
                </p>
              </div>
              <div className="bg-red-50 rounded-lg p-4">
                <span className="text-sm text-gray-500">Failed</span>
                <p className="text-xl font-bold text-red-700 mt-1">
                  {metrics.failedMessages?.toLocaleString()}
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default ExecutionDetailsPage
